import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { IVisitorLandingPageProps } from './types';
import { IWelcomePageTheme } from '../../admin/theme/form';
import { useSignalR } from '../../signalR';
import { ButtonContainer } from '../../form';
import NotificationService from '../../../services/NotificationService';
import ClientLogo from '../../clientLogo';
import CaptureItLogo from '../../../images/CaptureIT.png';
import styles from './VisitorLandingPage.module.css';
import { ThemedButton } from './VisitorLandingPage.styled';

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  text-align: center;
`;

// https://stackoverflow.com/questions/23583245/add-transition-while-changing-img-src-with-javascript
const Body = styled.div<IWelcomePageTheme>`
  padding: 1rem;
  position: relative;
  height: calc(100% - 68px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  color: ${(props) => props.textColor};
  z-index: 1;

  &::after {
    content: ' ';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: -1;
    transition: all 1s ease-in-out;

    opacity: ${({ opacityPercent }) => opacityPercent / 100};

    ${({ imageUrl }) => css`
      background: url(${imageUrl}) no-repeat;
      background-size: cover;
      background-position: center;
    `}
  }
`;

export const VisitorLandingPage: React.FC<IVisitorLandingPageProps> = ({
  pin,
  site,
  siteTheme,
  signInButtonColor,
  signInTextColor,
  signOutButtonColor,
  signOutTextColor,
  logoUrl,
  onSignIn,
  onSignOut,
}) => {
  const notificationService = useRef<NotificationService>(
    new NotificationService(),
  );

  const { connected, subscribe, unsubscribe } = useSignalR();

  // notifications
  useEffect(() => {
    notificationService.current.requestPermission();
  }, []);

  // subscribe to SignalR messages
  useEffect(() => {
    const handleReloadSiteMessageReceived = (sitePin: string) => {
      if (sitePin === pin) {
        window.location.reload();
      }
    };

    if (connected) {
      subscribe('ReceiveReloadSiteMessage', handleReloadSiteMessageReceived);

      return () => {
        unsubscribe('ReceiveReloadSiteMessage');
      };
    }
  }, [connected, subscribe, unsubscribe, pin]);

  return (
    <Container>
      <Body
        textColor={siteTheme.textColor}
        imageUrl={siteTheme.imageUrl}
        opacityPercent={siteTheme.opacityPercent}
      >
        <div>
          <h1>{siteTheme.text}</h1>
          <h2 className={styles.site}>{site}</h2>
        </div>
        <ButtonContainer align="center" retainOrderOnMobile>
          <ThemedButton
            type="button"
            background={signInButtonColor}
            color={signInTextColor}
            size="lg"
            onClick={() => onSignIn && onSignIn()}
          >
            Sign In
          </ThemedButton>
          <ThemedButton
            type="button"
            background={signOutButtonColor}
            color={signOutTextColor}
            size="lg"
            onClick={() => onSignOut && onSignOut()}
          >
            Sign Out
          </ThemedButton>
        </ButtonContainer>
      </Body>
      <footer className={styles.footer}>
        <ClientLogo imageUrl={logoUrl} />
        <img src={CaptureItLogo} alt="CaptureIT" />
      </footer>
    </Container>
  );
};
